import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollText, Lock } from "lucide-react";
import TicketAuditLog from "@/components/TicketAuditLog";
import TicketDetailsForm from "@/components/TicketDetailsForm";
import DisputePacketButton from "@/components/DisputePacketButton";

// Ticket record: editable details (locked once closed, owner-only) + the read-only audit trail.
export default function TicketRecordDialog({ sessionId, ticketNumber, compact = false, onChanged }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("audit");
  const [key, setKey] = useState(0);
  const [order, setOrder] = useState("desc");
  const [session, setSession] = useState(null);

  useEffect(() => {
    if (open) api.get(`/sessions/${sessionId}`).then(({ data }) => setSession(data));
  }, [open, sessionId, key]);

  const closed = !!session?.checkOutAt;
  const isOwner = user?.role === "owner";
  const canPacket = ["owner", "manager"].includes(user?.role);
  const refresh = () => setKey((k) => k + 1);

  return (
    <>
      <Button data-testid={`ticket-record-open-${sessionId}`} type="button" variant="outline" onClick={() => setOpen(true)}
        className={`gap-2 border-slate-700 bg-slate-950 text-slate-200 hover:bg-slate-800 ${compact ? "h-8 px-2 text-xs" : "h-10"}`}>
        <ScrollText className="h-4 w-4" /> {compact ? "Audit" : "Ticket & Audit"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="ticket-record-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto border-slate-800 bg-slate-900 text-slate-100">
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-3 font-heading text-xl">
              Ticket <span className="font-data text-[#d946ef]">{ticketNumber || "—"}</span>
              {closed && (
                <Badge data-testid="ticket-locked-badge" className="gap-1 bg-amber-600/90 hover:bg-amber-600">
                  <Lock className="h-3 w-3" /> Closed · Locked{isOwner ? " (owner can edit)" : ""}
                </Badge>
              )}
            </DialogTitle>
            {canPacket && <div className="pt-2"><DisputePacketButton sessionId={sessionId} ticketNumber={ticketNumber} onDone={refresh} /></div>}
          </DialogHeader>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-slate-950">
              <TabsTrigger data-testid="ticket-audit-tab" value="audit">Audit</TabsTrigger>
              <TabsTrigger data-testid="ticket-details-tab" value="details">{closed && !isOwner ? "Details" : "Edit details"}</TabsTrigger>
            </TabsList>
            <TabsContent value="audit"><TicketAuditLog sessionId={sessionId} refreshKey={key} order={order} setOrder={setOrder} /></TabsContent>
            <TabsContent value="details">
              <TicketDetailsForm session={session} locked={closed && !isOwner} onSaved={() => { refresh(); onChanged?.(); }} />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
}
